import { useState } from 'react';
import { api } from '../api/client';
import type { Membership } from '../api/types';
import { Alert, formatDate, money } from '../components/common';

/**
 * The counter screen: find a member, take a drink off their allowance.
 *
 * Built for someone with a queue in front of them. The refusal reason comes
 * from the server as a sentence, so whoever is serving can read it out rather
 * than work out from a red icon why the button will not press.
 */
export function StaffMembershipsPage() {
  const [term, setTerm] = useState('');
  const [results, setResults] = useState<Membership[] | null>(null);
  const [item, setItem] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function search() {
    if (term.trim().length < 2) return;

    setBusy(true);
    setError(null);
    setDone(null);

    try {
      setResults(await api.membershipLookup(term.trim()));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  async function redeem(membership: Membership) {
    setBusy(true);
    setError(null);
    setDone(null);

    try {
      const updated = await api.membershipRedeem(membership.id, item.trim() || 'Drink');

      setResults((current) => current?.map((m) => (m.id === updated.id ? updated : m)) ?? null);
      setDone(
        `Done — ${updated.customerName} has ${updated.drinksRemaining} of ${updated.drinkAllowance} left.`,
      );
      setItem('');
    } catch (e) {
      // The server's refusal is written to be said out loud.
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="page page--narrow">
      <h1>Memberships</h1>
      <p className="muted small">
        Members collect their drinks here. Delivery is a normal paid order — the membership does not
        cover it.
      </p>

      {error && <Alert>{error}</Alert>}
      {done && <Alert tone="success">{done}</Alert>}

      <label className="field">
        <span>Find a member</span>
        <input
          type="search"
          value={term}
          placeholder="Membership number, name or phone"
          onChange={(e) => setTerm(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && void search()}
        />
      </label>

      <button type="button" className="btn btn--primary btn--block" disabled={busy} onClick={search}>
        Search
      </button>

      {results?.length === 0 && <Alert tone="info">No membership matches that.</Alert>}

      {results?.map((m) => (
        <div key={m.id} className="panel">
          <div className="memcard__head">
            <div>
              <strong>{m.customerName}</strong>
              <p className="muted small">
                {m.membershipNumber} · {m.planName}
              </p>
            </div>
            <span className={m.isCurrent ? 'pill pill--good' : 'pill pill--bad'}>
              {m.isCurrent ? 'Active' : m.status}
            </span>
          </div>

          <div className="stats">
            <div>
              <strong>{m.drinksRemaining}</strong>
              <span className="muted small">left this month</span>
            </div>
            <div>
              <strong>{m.usedToday}</strong>
              <span className="muted small">used today (max {m.maxRedemptionsPerDay})</span>
            </div>
            <div>
              <strong>{m.memberDiscountPercent}%</strong>
              <span className="muted small">off everything else</span>
            </div>
          </div>

          {m.blockedReason ? (
            <Alert>{m.blockedReason}</Alert>
          ) : (
            <>
              <label className="field">
                <span>What are they taking?</span>
                <input
                  type="text"
                  value={item}
                  placeholder="Berry Blast 350ml"
                  onChange={(e) => setItem(e.target.value)}
                />
              </label>

              <button
                type="button"
                className="btn btn--primary btn--block"
                disabled={busy}
                onClick={() => redeem(m)}
              >
                Take one off the allowance
              </button>
            </>
          )}

          <p className="muted small">
            Paid {money(m.pricePaid)} · runs to {formatDate(m.endsAt)}
          </p>

          {m.recent.length > 0 && (
            <details>
              <summary>Recent ({m.recent.length})</summary>
              <ul className="activity">
                {m.recent.slice(0, 8).map((r, i) => (
                  <li key={i}>
                    <span>{r.itemName}</span>
                    <span className="muted small">{formatDate(r.redeemedAt)}</span>
                  </li>
                ))}
              </ul>
            </details>
          )}
        </div>
      ))}
    </section>
  );
}
